import { Container, CssBaseline, Stack, ThemeProvider } from '@mui/material';
import Header from 'Components/Layout/Header';
import Footer from 'Components/Layout/Footer';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import React, { useEffect, useMemo, useState } from 'react';
import SEO from 'Components/SEO';
import { useTranslation } from 'react-i18next';
import loadable from '@loadable/component';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import {
  GameContext,
  LoginContext,
  ThemeContext,
  createGuestUser,
  User,
  UserContext,
  DEFAULT_LOTTERY_ID,
  getLotteryRulesById,
  getLotteryById,
  getLotteryRoutePath,
  getSupportedLotteries,
  isAuthenticatedUser,
  resolveLotteryId,
  resolveLotteryIdFromRouteSlug,
} from 'Components/Context';
import { buildRandoStatTheme } from 'theme';
import { localStorageService } from 'services/LocalStorage';
import { LocationContextService } from 'services/LocationContext';
import { Config } from 'services/Config';
import { UserService } from "services/Users";
import { buildNumberSignalsSeo } from 'services/seo/numberSignalsSeo';
import LNG_MAP from 'i18n/languages';
import { isReactSnapPrerender } from 'services/utils';
import { loadAmplifyAuth, signOutUser } from 'services/AuthRuntime';
import {
  isSupportedCountryCode,
  normalizeCountryCode,
  resolveDefaultLotteryForCountry,
} from 'Components/Layout/Header/countryLotteryConfig';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 10, // 10 minutes
      staleTime: 1000 * 60 * 2, // 2 minutes
      retry: isReactSnapPrerender() ? false : 3,
    },
  },
})

const persister = typeof window !== "undefined"
  ? createAsyncStoragePersister({ storage: window.localStorage })
  : null;


const Mission = loadable(() => import("./Components/Pages/mission"));
const Home = loadable(() => import("./Components/Layout/Home"));
const BeforeYouWin = loadable(() => import("./Components/Pages/beforeYouWin"));
const Terms = loadable(() => import("./Components/Pages/terms"));
const Privacy = loadable(() => import("./Components/Pages/privacy"));
const HowItWorks = loadable(() => import("./Components/Pages/howItWorks"));
const LearnLotteryPage = loadable(() => import("./Components/Pages/learnLottery"));
const MathPage = loadable(() => import("./Components/Math"));
const NumberSignalsPage = loadable(() => import("./Components/Pages/numberSignals"));
const ErrorPage = loadable(() => import("./Components/Error"));
const Login = loadable(() => import("./Components/Login"));
const PricingPage = loadable(() => import("./Components/Pricing"));
const EmbeddedCheckoutPage = loadable(() => import("./Components/Checkout/Embedded"));
const CheckoutReturnPage = loadable(() => import("./Components/Checkout/Return"));
const SettingsPage = loadable(() => import("./Components/Settings"));
// const Terms = prerenderedLoadable(() => import("./Components/Layout/Terms"));
// const Privacy = prerenderedLoadable(() => import("./Components/Layout/Privacy"));
// const CheckoutForm = lazy(() => import('./Components/Payment'));

const SUPPORTED_LOCALES = Object.freeze(Object.keys(LNG_MAP));

const resolveLocale = (value, fallback = "en") => {
  const normalizedLocale = typeof value === "string" ? value.toLowerCase().trim() : "";
  if (SUPPORTED_LOCALES.includes(normalizedLocale)) {
    return normalizedLocale;
  }

  const normalizedFallback = typeof fallback === "string" ? fallback.toLowerCase().trim() : "";
  return SUPPORTED_LOCALES.includes(normalizedFallback) ? normalizedFallback : "en";
};

const DEFAULT_COUNTRY_CODE = "US";
const DEFAULT_BASE_URL = "https://randostat.com";
const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";
const LIGHT_BG_COLOR = "#f3f2ef";
const DARK_BG_COLOR = "#121826";

const getInitialSystemPrefersDarkMode = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  try {
    return !!window.matchMedia(COLOR_SCHEME_QUERY).matches;
  } catch (_error) {
    return false;
  }
};

const getInitialThemeMode = () => {
  const storedThemeMode = localStorageService.getThemeMode();
  return ["light", "dark", "system"].includes(storedThemeMode)
    ? storedThemeMode
    : "system";
};

const getSeoBaseUrl = () => (Config.getBaseUrl() || DEFAULT_BASE_URL).replace(/\/+$/, "");

const isOAuthCallbackRequest = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const params = new URLSearchParams(window.location.search || "");
  return params.has("code") || params.has("error") || params.has("state");
};

const buildLocalizedUrl = (locale, path = "") => {
  const normalizedLocale = resolveLocale(locale, "en");
  const normalizedPath = typeof path === "string"
    ? path.trim().replace(/^\/+|\/+$/g, "")
    : "";

  const baseUrl = getSeoBaseUrl();
  if (!normalizedPath) {
    return `${baseUrl}/${normalizedLocale}`;
  }
  return `${baseUrl}/${normalizedLocale}/${normalizedPath}`;
};

const resolveTranslatedLabel = (t, key, fallback) => {
  const translated = typeof t === "function"
    ? t(key, { defaultValue: fallback })
    : "";

  return translated && translated !== key ? translated : fallback;
};

const buildSiteStructuredData = (description) => {
  const siteUrl = getSeoBaseUrl();
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "RandoStat",
      url: siteUrl,
      applicationCategory: "UtilitiesApplication",
      operatingSystem: "Web",
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "RandoStat",
      url: siteUrl,
      logo: `${siteUrl}/logo192.png`,
    },
  ];
};

const buildBreadcrumbStructuredData = (locale, items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: buildLocalizedUrl(locale, item.path),
  })),
});

const buildArticleStructuredData = ({ locale, path, title, description, dateModified = "2026-05-21" }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  dateModified,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": buildLocalizedUrl(locale, path),
  },
  publisher: {
    "@type": "Organization",
    name: "RandoStat",
    url: getSeoBaseUrl(),
  },
});

const getStoredCountryContext = () => {
  const selectedCountry = normalizeCountryCode(localStorageService.getSelectedCountryCode());
  const detectedCountry = normalizeCountryCode(localStorageService.getDetectedCountryCode());

  if (selectedCountry && isSupportedCountryCode(selectedCountry)) {
    return { country: selectedCountry, source: "selected_country" };
  }

  if (detectedCountry && isSupportedCountryCode(detectedCountry)) {
    return { country: detectedCountry, source: "detected_country" };
  }

  return { country: "", source: "" };
};

const getCountryHintFromTimezone = () => {
  const timezone = typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : "";

  return timezone === "Africa/Johannesburg" ? "ZA" : "";
};

const createGuestUserFromStorage = () => {
  const storedCountryContext = getStoredCountryContext();
  if (!storedCountryContext.country) {
    return createGuestUser({
      preferences: {
        country: "",
      },
    });
  }

  return createGuestUser({
    preferences: {
      country: storedCountryContext.country,
    },
    market: {
      country: storedCountryContext.country,
      country_source: storedCountryContext.source === "selected_country" ? "selected" : "detected",
    },
  });
};

const LotteryRoute = ({ fallbackLocale, resolvePreferredLotteryId, selectedLotteryId, setSelectedLotteryId }) => {
  const { lng, lotterySlug } = useParams();
  const { t } = useTranslation("seo");
  const { t: tCommon } = useTranslation("common");
  const resolvedLocale = resolveLocale(lng, fallbackLocale);
  const resolvedLotteryId = resolveLotteryIdFromRouteSlug(lotterySlug, "");
  const resolvedLottery = getLotteryById(resolvedLotteryId);
  const routeSlug = resolvedLottery.routeSlug;
  const ogImagePath = `/og/${resolvedLottery.id}-og.png`;
  const fallbackLotteryName = getLotteryRulesById(resolvedLottery.id).name || resolvedLottery.id;
  const lotteryName = resolveTranslatedLabel(tCommon, resolvedLottery.labelKey, fallbackLotteryName);
  const lotteryStatisticsTitle = t("seo.lotteryStatistics.title", {
    lotteryName,
    defaultValue: `${lotteryName} | RandoStat`,
  });
  const lotteryStatisticsDescription = t("seo.lotteryStatistics.description", {
    lotteryName,
    defaultValue: `${lotteryName} draw history, hot and cold numbers, frequency trends, and historical patterns in one interactive chart.`,
  });
  const statsStructuredData = [
    ...buildSiteStructuredData(
      t("seo.home.description", {
        defaultValue: "RandoStat offers lottery statistics, educational content, and historical number signals.",
      })
    ),
    buildBreadcrumbStructuredData(resolvedLocale, [
      {
        name: t("seo.home.breadcrumb", { defaultValue: "Home" }),
        path: "",
      },
      {
        name: lotteryName,
        path: routeSlug,
      },
    ]),
  ];

  useEffect(() => {
    if (resolvedLotteryId && resolvedLotteryId !== selectedLotteryId) {
      setSelectedLotteryId(resolvedLotteryId, { explicit: true });
    }
  }, [resolvedLotteryId, selectedLotteryId, setSelectedLotteryId]);

  if (resolvedLocale !== (lng || "").toLowerCase()) {
    if (resolvedLotteryId) {
      return <Navigate replace to={getLotteryRoutePath(resolvedLocale, resolvedLotteryId)} />;
    }
    return <Navigate replace to={getLotteryRoutePath(resolvedLocale, resolvePreferredLotteryId())} />;
  }

  if (!resolvedLotteryId) {
    if (lotterySlug) {
      console.warn("Unsupported lottery slug in route", lotterySlug);
    }
    const fallbackLotteryId = resolvePreferredLotteryId();
    return <Navigate replace to={getLotteryRoutePath(resolvedLocale, fallbackLotteryId)} />;
  }

  return (
    <SEO
      title={lotteryStatisticsTitle}
      description={lotteryStatisticsDescription}
      path={routeSlug}
      imagePath={ogImagePath}
      structuredData={statsStructuredData}
      element={<MathPage gameName={resolvedLotteryId} />}
    />
  );
};

const NumberSignalsRoute = ({ fallbackLocale, resolvePreferredLotteryId, selectedLotteryId, setSelectedLotteryId }) => {
  const { lng, lotterySlug } = useParams();
  const { t } = useTranslation("seo");
  const { t: tCommon } = useTranslation("common");
  const resolvedLocale = resolveLocale(lng, fallbackLocale);
  const resolvedLotteryId = resolveLotteryIdFromRouteSlug(lotterySlug, "");
  const resolvedLottery = getLotteryById(resolvedLotteryId);
  const routeSlug = resolvedLottery.routeSlug;
  const numberSignalsPath = `${routeSlug}/number-signals`;
  const ogImagePath = `/og/${resolvedLottery.id}-og.png`;
  const fallbackLotteryName = getLotteryRulesById(resolvedLottery.id).name || resolvedLottery.id;
  const lotteryName = resolveTranslatedLabel(tCommon, resolvedLottery.labelKey, fallbackLotteryName);
  const numberSignalsSeo = buildNumberSignalsSeo({
    lotteryId: resolvedLotteryId,
    lotteryName,
    locale: resolvedLocale,
    year: new Date().getUTCFullYear(),
    t,
  });
  const numberSignalsStructuredData = [
    ...buildSiteStructuredData(
      t("seo.home.description", {
        defaultValue: "RandoStat offers lottery statistics, educational content, and historical number signals.",
      })
    ),
    buildBreadcrumbStructuredData(resolvedLocale, [
      {
        name: t("seo.home.breadcrumb", { defaultValue: "Home" }),
        path: "",
      },
      {
        name: lotteryName,
        path: routeSlug,
      },
      {
        name: t("seo.numberSignals.breadcrumb", { defaultValue: "Number Signals" }),
        path: numberSignalsPath,
      },
    ]),
  ];

  useEffect(() => {
    if (resolvedLotteryId && resolvedLotteryId !== selectedLotteryId) {
      setSelectedLotteryId(resolvedLotteryId, { explicit: true });
    }
  }, [resolvedLotteryId, selectedLotteryId, setSelectedLotteryId]);

  if (resolvedLocale !== (lng || "").toLowerCase()) {
    if (resolvedLotteryId) {
      return <Navigate replace to={`/${resolvedLocale}/${numberSignalsPath}`} />;
    }
    return <Navigate replace to={getLotteryRoutePath(resolvedLocale, resolvePreferredLotteryId())} />;
  }

  if (!resolvedLotteryId) {
    if (lotterySlug) {
      console.warn("Unsupported lottery slug in route", lotterySlug);
    }
    const fallbackLotteryId = resolvePreferredLotteryId();
    return <Navigate replace to={getLotteryRoutePath(resolvedLocale, fallbackLotteryId)} />;
  }

  return (
    <SEO
      title={numberSignalsSeo.title}
      description={numberSignalsSeo.description}
      path={numberSignalsPath}
      imagePath={ogImagePath}
      structuredData={numberSignalsStructuredData}
      element={(
        <NumberSignalsPage
          lotteryId={resolvedLotteryId}
          lotteryName={lotteryName}
          locale={resolvedLocale}
        />
      )}
    />
  );
};

const LocaleEntryRedirect = ({ fallbackLocale, resolvePreferredLotteryId }) => {
  const { lng } = useParams();
  const lotteryIdFromSlug = resolveLotteryIdFromRouteSlug(lng, "");
  if (lotteryIdFromSlug) {
    const localeFromFallback = resolveLocale(fallbackLocale, "en");
    return <Navigate replace to={getLotteryRoutePath(localeFromFallback, lotteryIdFromSlug)} />;
  }

  const locale = resolveLocale(lng, fallbackLocale);
  return <Navigate replace to={getLotteryRoutePath(locale, resolvePreferredLotteryId())} />;
};

const CallbackAwareRedirect = ({ to }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search || "");
  const hasOAuthParams = queryParams.has("code") || queryParams.has("state") || queryParams.has("error");
  const nextPath = hasOAuthParams
    ? `${to}${location.search || ""}${location.hash || ""}`
    : to;

  return <Navigate replace to={nextPath} />;
};

// const LegacyLotteryStatisticsRedirect = ({ fallbackLocale, resolvePreferredLotteryId }) => {
//   const { lng } = useParams();
//   const locale = resolveLocale(lng, fallbackLocale);
//   return <Navigate replace to={getLotteryRoutePath(locale, resolvePreferredLotteryId())} />;
// };

function App() {
  const { i18n } = useTranslation("common");
  const { t } = useTranslation("seo");
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const [systemPrefersDarkMode, setSystemPrefersDarkMode] = useState(() => getInitialSystemPrefersDarkMode());
  const [selectedLotteryIdState, setSelectedLotteryIdState] = useState(DEFAULT_LOTTERY_ID);
  const [hasExplicitLotterySelection, setHasExplicitLotterySelection] = useState(false);
  const [effectiveCountryState, setEffectiveCountryState] = useState({
    country: DEFAULT_COUNTRY_CODE,
    source: "default",
  });

  const setSelectedLotteryId = React.useCallback((lotteryId, options = {}) => {
    const { explicit = false } = options;
    const resolvedLotteryId = resolveLotteryId(lotteryId, DEFAULT_LOTTERY_ID);
    setSelectedLotteryIdState((previousLotteryId) => (
      previousLotteryId === resolvedLotteryId ? previousLotteryId : resolvedLotteryId
    ));

    if (explicit) {
      setHasExplicitLotterySelection(true);
      localStorageService.setSelectedLotteryId(resolvedLotteryId);
    }
  }, []);

  const [gameRules, setGameRules] = useState(() => getLotteryRulesById(selectedLotteryIdState));
  const supportedLotteries = useMemo(() => getSupportedLotteries(), []);
  const rulesProvider = useMemo(() => ({
    gameRules,
    setGameRules,
    selectedLotteryId: selectedLotteryIdState,
    setSelectedLotteryId,
    supportedLotteries,
  }), [gameRules, selectedLotteryIdState, setSelectedLotteryId, supportedLotteries]);

  const [user, setUser] = useState(() => createGuestUser({
    preferences: {
      country: "",
    },
  }));
  const [userResolved, setUserResolved] = useState(false);
  const userProvider = { user, userResolved, setUser };
  const [triggerLogin, setTriggerLogin] = useState(false);
  const loginProvider = { triggerLogin, setTriggerLogin };
  const [themeMode, setThemeMode] = useState(() => getInitialThemeMode());
  const resolvePreferredLotteryId = () => {
    const userLotteryId = isAuthenticatedUser(user)
      ? resolveLotteryId(user?.preferences?.selected_lottery, "")
      : "";
    if (userLotteryId) {
      return userLotteryId;
    }

    const explicitLotteryId = resolveLotteryId(localStorageService.getSelectedLotteryId(), "");
    if (explicitLotteryId) {
      return explicitLotteryId;
    }

    const countryDefaultLotteryId = resolveDefaultLotteryForCountry(
      effectiveCountryState.country,
      DEFAULT_LOTTERY_ID
    );
    return resolveLotteryId(
      selectedLotteryIdState || countryDefaultLotteryId,
      DEFAULT_LOTTERY_ID
    );
  };

  useEffect(() => {
    const resolvedLotteryId = resolveLotteryId(selectedLotteryIdState, DEFAULT_LOTTERY_ID);
    setGameRules(getLotteryRulesById(resolvedLotteryId));
  }, [selectedLotteryIdState]);

  useEffect(() => {
    const storedThemeMode = localStorageService.getThemeMode();
    if (storedThemeMode) {
      setThemeMode((previousMode) => (previousMode === storedThemeMode ? previousMode : storedThemeMode));
    }

    const storedLotteryId = resolveLotteryId(localStorageService.getSelectedLotteryId(), "");
    if (storedLotteryId) {
      setHasExplicitLotterySelection(true);
      setSelectedLotteryIdState((previousLotteryId) => (
        previousLotteryId === storedLotteryId ? previousLotteryId : storedLotteryId
      ));
    }

    setUser(createGuestUserFromStorage());
  }, []);

  const handleThemeModeChange = (mode, options = {}) => {
    const { persist = true } = options;
    localStorageService.setThemeMode(mode);
    setThemeMode(mode);

    if (!persist) {
      return;
    }

    const nextPreferences = UserService.persistUserPreferences(user, { theme: mode });
    setUser((previousUser) => ({
      ...previousUser,
      preferences: UserService.mergePreferences(previousUser?.preferences, nextPreferences),
    }));
  };

  const applyUserPreferences = async (preferences) => {
    if (!preferences || typeof preferences !== "object") {
      return;
    }

    if (["light", "dark", "system"].includes(preferences.theme)) {
      handleThemeModeChange(preferences.theme, { persist: false });
    }

    const preferredLanguage = typeof preferences.language === "string"
      ? preferences.language.toLowerCase()
      : null;

    if (preferredLanguage && preferredLanguage !== i18n.language) {
      await i18n.changeLanguage(preferredLanguage);
    }

    const preferredCountry = typeof preferences.country === "string"
      ? preferences.country.toUpperCase()
      : "";
    if (preferredCountry) {
      localStorageService.setSelectedCountryCode(preferredCountry);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(COLOR_SCHEME_QUERY);
    const handleChange = (event) => {
      setSystemPrefersDarkMode(event.matches);
    };

    setSystemPrefersDarkMode(mediaQuery.matches);
    mediaQuery.addEventListener?.("change", handleChange);

    return () => {
      mediaQuery.removeEventListener?.("change", handleChange);
    };
  }, []);

  useEffect(() => {
    if (!userResolved) {
      return;
    }

    let cancelled = false;

    const resolveEffectiveCountry = async () => {
      const userCountry = normalizeCountryCode(
        user?.market?.country || user?.preferences?.country || ""
      );
      if (userCountry && isSupportedCountryCode(userCountry)) {
        if (!cancelled) {
          setEffectiveCountryState({ country: userCountry, source: "user_profile" });
        }
        return;
      }

      const selectedCountry = normalizeCountryCode(localStorageService.getSelectedCountryCode());
      if (selectedCountry && isSupportedCountryCode(selectedCountry)) {
        if (!cancelled) {
          setEffectiveCountryState({ country: selectedCountry, source: "selected_country" });
        }
        return;
      }

      const detectedCountry = normalizeCountryCode(localStorageService.getDetectedCountryCode());
      if (detectedCountry && isSupportedCountryCode(detectedCountry)) {
        if (!cancelled) {
          setEffectiveCountryState({ country: detectedCountry, source: "detected_country" });
        }
        return;
      }

      if (isReactSnapPrerender()) {
        if (!cancelled) {
          setEffectiveCountryState({ country: DEFAULT_COUNTRY_CODE, source: "prerender" });
        }
        return;
      }

      try {
        const locationContext = await LocationContextService.getEstimatedCountry();
        if (cancelled) {
          return;
        }

        const estimatedCountry = normalizeCountryCode(locationContext?.estimated_country || "");
        if (estimatedCountry && isSupportedCountryCode(estimatedCountry)) {
          if (!localStorageService.getSelectedCountryCode()) {
            localStorageService.setDetectedCountryCode(estimatedCountry);
          }
          setEffectiveCountryState({ country: estimatedCountry, source: "location_context" });
          return;
        }
      } catch (error) {
        console.error("Failed to load location context:", error);
      }

      const timezoneCountryHint = getCountryHintFromTimezone();
      if (timezoneCountryHint && isSupportedCountryCode(timezoneCountryHint)) {
        if (!cancelled) {
          setEffectiveCountryState({ country: timezoneCountryHint, source: "timezone" });
        }
        return;
      }

      if (!cancelled) {
        if (!localStorageService.getDetectedCountryCode()) {
          localStorageService.setDetectedCountryCode(DEFAULT_COUNTRY_CODE);
        }
        setEffectiveCountryState({ country: DEFAULT_COUNTRY_CODE, source: "default" });
      }
    };

    resolveEffectiveCountry();

    return () => {
      cancelled = true;
    };
  }, [userResolved, user?.market?.country, user?.preferences?.country]);

  useEffect(() => {
    const explicitUserLotteryId = isAuthenticatedUser(user)
      ? resolveLotteryId(user?.preferences?.selected_lottery, "")
      : "";
    if (explicitUserLotteryId) {
      if (!hasExplicitLotterySelection) {
        setHasExplicitLotterySelection(true);
      }
      if (!hasExplicitLotterySelection && explicitUserLotteryId !== selectedLotteryIdState) {
        setSelectedLotteryId(explicitUserLotteryId, { explicit: false });
      }
      return;
    }

    if (hasExplicitLotterySelection) {
      return;
    }

    const defaultLotteryId = resolveDefaultLotteryForCountry(
      effectiveCountryState.country,
      DEFAULT_LOTTERY_ID
    );
    if (defaultLotteryId && defaultLotteryId !== selectedLotteryIdState) {
      setSelectedLotteryId(defaultLotteryId, { explicit: false });
    }
  }, [
    effectiveCountryState.country,
    hasExplicitLotterySelection,
    selectedLotteryIdState,
    setSelectedLotteryId,
    user,
  ]);

  const effectivePrefersDarkMode = themeMode === "system"
    ? systemPrefersDarkMode
    : themeMode === "dark";

  const themeProvider = { themeMode, setThemeMode: handleThemeModeChange };

  const theme = useMemo(
    () => {
      return buildRandoStatTheme(effectivePrefersDarkMode);
    },
    [effectivePrefersDarkMode]
  );

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const root = document.documentElement;
    const backgroundColor = effectivePrefersDarkMode ? DARK_BG_COLOR : LIGHT_BG_COLOR;
    root.setAttribute("data-rs-boot-theme", effectivePrefersDarkMode ? "dark" : "light");
    root.style.backgroundColor = backgroundColor;

    if (document.body) {
      document.body.style.backgroundColor = backgroundColor;
    }

    const themeColorMeta = document.querySelector("meta[name='theme-color']");
    if (themeColorMeta) {
      themeColorMeta.setAttribute("content", backgroundColor);
    }

    document.documentElement.removeAttribute("data-rs-hide-root");
    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.style.visibility = "visible";
    }
  }, [effectivePrefersDarkMode]);

  useEffect(() => {
    let isCancelled = false;
    let idleCallbackId = null;
    let timeoutId = null;
    const oauthCallbackMode = isOAuthCallbackRequest();

    const wait = (ms) => new Promise((resolve) => {
      if (typeof window !== "undefined" && typeof window.setTimeout === "function") {
        window.setTimeout(resolve, ms);
        return;
      }
      setTimeout(resolve, ms);
    });

    const restoreUserSession = async () => {
      if (isReactSnapPrerender()) {
        setUser(createGuestUserFromStorage());
        setUserResolved(true);
        return;
      }

      try {
        const authModule = await loadAmplifyAuth();
        const { getCurrentUser, fetchUserAttributes } = authModule;
        const maxSessionAttempts = oauthCallbackMode ? 12 : 1;
        let currentUser = null;
        let userAttributes = null;

        for (let attempt = 1; attempt <= maxSessionAttempts; attempt += 1) {
          if (isCancelled) {
            return;
          }

          try {
            currentUser = await getCurrentUser();
            if (currentUser) {
              userAttributes = await fetchUserAttributes().catch(() => null);
              break;
            }
          } catch (_error) {
            // Ignore until retries are exhausted on OAuth callback.
          }

          if (!oauthCallbackMode || attempt === maxSessionAttempts) {
            currentUser = null;
            break;
          }

          await wait(300);
        }

        // console.log("Current user: ", currentUser, "; attributes: ", userAttributes);
        if (isCancelled) return;

        if (!currentUser) {
          setUser(createGuestUserFromStorage());
          return;
        }

        const fallbackUser = new User({
          user_id: userAttributes?.sub || currentUser?.userId || currentUser?.username || null,
          name: userAttributes?.name ?? userAttributes?.email ?? currentUser?.username ?? null,
          email: userAttributes?.email ?? null,
          picture: userAttributes?.picture ?? null,
          signOut: signOutUser,
          access: { tier: "public" },
        });
        let userObj = fallbackUser;

        try {
          const userData = await UserService.getUser();
          // console.log("Fetched user data: ", userData);
          if (!isCancelled && userData) {
            userObj = new User({
              ...userData,
              picture: userAttributes?.picture ?? userData?.picture ?? null,
              signOut: signOutUser,
            });
            userObj.preferences = UserService.getResolvedPreferences(
              userObj,
              userObj.preferences,
              fallbackUser.preferences
            );
          }
        } catch (error) {
          console.error("Failed to restore user data: ", error);
        }

        if (isCancelled) return;
        await applyUserPreferences(userObj.preferences);
        if (isCancelled) return;
        setUser(userObj);
      } catch {
        if (isCancelled) return;
        setUser(createGuestUserFromStorage());
      } finally {
        if (isCancelled) return;
        setUserResolved(true);
      }
    };

    const runRestore = () => {
      restoreUserSession();
    };

    if (oauthCallbackMode) {
      runRestore();
    } else if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      idleCallbackId = window.requestIdleCallback(runRestore, { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(runRestore, 120);
    }

    return () => {
      isCancelled = true;
      if (typeof window !== "undefined") {
        if (idleCallbackId !== null && typeof window.cancelIdleCallback === "function") {
          window.cancelIdleCallback(idleCallbackId);
        }
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!userResolved || !isAuthenticatedUser(user) || !hasExplicitLotterySelection) {
      return;
    }

    const normalizedLotteryId = resolveLotteryId(selectedLotteryIdState, DEFAULT_LOTTERY_ID);
    const userSelectedLotteryId = resolveLotteryId(user?.preferences?.selected_lottery, "");
    if (userSelectedLotteryId === normalizedLotteryId) {
      return;
    }

    const nextPreferences = UserService.persistUserPreferences(user, {
      selected_lottery: normalizedLotteryId,
    });
    setUser((previousUser) => ({
      ...previousUser,
      preferences: UserService.mergePreferences(previousUser?.preferences, nextPreferences),
    }));
  }, [hasExplicitLotterySelection, selectedLotteryIdState, user, userResolved]);

  const resolvedCurrentLocale = resolveLocale(currentLanguage, "en");
  const siteDescription = t("seo.home.description", {
    defaultValue: "RandoStat offers in-depth lottery statistics, debunks myths, and explains randomness without prediction claims.",
  });
  const siteStructuredData = buildSiteStructuredData(siteDescription);
  const homeStructuredData = [
    ...siteStructuredData,
    buildBreadcrumbStructuredData(resolvedCurrentLocale, [
      {
        name: t("seo.home.breadcrumb", { defaultValue: "Home" }),
        path: "",
      },
    ]),
  ];
  const mythsStructuredData = [
    ...siteStructuredData,
    buildArticleStructuredData({
      locale: resolvedCurrentLocale,
      path: "learn/lottery-myths-odds-randomness",
      title: t("seo.myths.title", { defaultValue: "Lottery Myths and Probability | RandoStat" }),
      description: t("seo.myths.description", {
        defaultValue: "Explore lottery myths and probability principles to better understand random outcomes.",
      }),
    }),
    buildBreadcrumbStructuredData(resolvedCurrentLocale, [
      { name: t("seo.home.breadcrumb", { defaultValue: "Home" }), path: "" },
      {
        name: t("seo.myths.breadcrumb", { defaultValue: "Lottery Myths" }),
        path: "learn/lottery-myths-odds-randomness",
      },
    ]),
  ];
  const beforeYouWinStructuredData = [
    ...siteStructuredData,
    buildArticleStructuredData({
      locale: resolvedCurrentLocale,
      path: "before-you-win",
      title: t("seo.beforeYouWin.title", {
        defaultValue: "Before You Win - What Happens After Winning the Lottery | RandoStat",
      }),
      description: t("seo.beforeYouWin.description", {
        defaultValue: "Learn what happens after a major lottery win and how to approach it responsibly.",
      }),
    }),
    buildBreadcrumbStructuredData(resolvedCurrentLocale, [
      { name: t("seo.home.breadcrumb", { defaultValue: "Home" }), path: "" },
      {
        name: t("seo.beforeYouWin.breadcrumb", { defaultValue: "Before You Win" }),
        path: "before-you-win",
      },
    ]),
  ];
  const missionStructuredData = [
    ...siteStructuredData,
    buildArticleStructuredData({
      locale: resolvedCurrentLocale,
      path: "mission",
      title: t("seo.mission.title", { defaultValue: "RandoStat Mission | Lottery Education, Not Prediction" }),
      description: t("seo.mission.description", {
        defaultValue: "Understand why RandoStat focuses on transparency, randomness education, and responsible play.",
      }),
    }),
    buildBreadcrumbStructuredData(resolvedCurrentLocale, [
      { name: t("seo.home.breadcrumb", { defaultValue: "Home" }), path: "" },
      { name: t("seo.mission.breadcrumb", { defaultValue: "Mission" }), path: "mission" },
    ]),
  ];
  const howItWorksStructuredData = [
    ...siteStructuredData,
    buildArticleStructuredData({
      locale: resolvedCurrentLocale,
      path: "how-it-works",
      title: t("seo.howItWorks.title", { defaultValue: "How RandoStat Works | Lottery Statistics and Line Tools" }),
      description: t("seo.howItWorks.description", {
        defaultValue: "Learn how RandoStat transforms historical draws into understandable frequency and gap signals.",
      }),
    }),
    buildBreadcrumbStructuredData(resolvedCurrentLocale, [
      { name: t("seo.home.breadcrumb", { defaultValue: "Home" }), path: "" },
      { name: t("seo.howItWorks.breadcrumb", { defaultValue: "How It Works" }), path: "how-it-works" },
    ]),
  ];
  const pricingStructuredData = [
    ...siteStructuredData,
    buildBreadcrumbStructuredData(resolvedCurrentLocale, [
      { name: t("seo.home.breadcrumb", { defaultValue: "Home" }), path: "" },
      { name: t("seo.pricing.breadcrumb", { defaultValue: "Pricing" }), path: "pricing" },
    ]),
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persister ? { persister } : undefined}
      >
        <Container id="main-box" maxWidth="lg" sx={{ display: "flex", height: "100%", padding: "0px" }}>
          <Stack id="main-stack" style={{
            flexGrow: "1",
          }}>
            <GameContext.Provider value={rulesProvider}>
              <UserContext.Provider value={userProvider}>
                <LoginContext.Provider value={loginProvider}>
                  <ThemeContext.Provider value={themeProvider}>
                    <BrowserRouter>
                      <Header />
                      <Container
                        // fixed
                        sx={{
                          // display: "flex",
                          justifyContent: "center",
                          flexGrow: 1,
                          alignContent: "center",
                          p: 0,
                          // height: "100%",
                        }}>
                        <Routes>
                          <Route path="/" element={<CallbackAwareRedirect to={`/${currentLanguage}`} />} />
                          <Route path="/login" element={<CallbackAwareRedirect to={`/${currentLanguage}/login`} />} />
                          <Route path="/pricing" element={<CallbackAwareRedirect to={`/${currentLanguage}/pricing`} />} />
                          <Route path="/checkout" element={<CallbackAwareRedirect to={`/${currentLanguage}/checkout`} />} />
                          <Route path="/checkoutReturn" element={<CallbackAwareRedirect to={`/${currentLanguage}/checkoutReturn`} />} />
                          <Route path="/settings" element={<CallbackAwareRedirect to={`/${currentLanguage}/settings`} />} />
                          <Route
                            path="/stats"
                            element={<CallbackAwareRedirect to={getLotteryRoutePath(currentLanguage, resolvePreferredLotteryId())} />}
                          />
                          <Route
                            path="/lottery-statistics"
                            element={<CallbackAwareRedirect to={getLotteryRoutePath(currentLanguage, resolvePreferredLotteryId())} />}
                          />
                          <Route path="/:lng">
                            {/* <Route
                              path=""
                              element={(
                                <LocaleEntryRedirect
                                  fallbackLocale={resolveLocale(currentLanguage)}
                                  resolvePreferredLotteryId={resolvePreferredLotteryId}
                                />
                              )}
                            /> */}
                            <Route
                              path="before-you-win"
                              element={
                                <SEO
                                  title={t("seo.beforeYouWin.title")}
                                  description={t("seo.beforeYouWin.description")}
                                  path={"before-you-win"}
                                  structuredData={beforeYouWinStructuredData}
                                  element={<BeforeYouWin />}
                                />
                              }
                            />
                            <Route path="" element={
                              <SEO
                                title={t("seo.home.title")}
                                description={t("seo.home.description")}
                                path={""}
                                structuredData={homeStructuredData}
                                element={<Home />}
                              />
                            } />
                            <Route
                              path="mission"
                              element={(
                                <SEO
                                  title={t("seo.mission.title", { defaultValue: "RandoStat Mission | Lottery Education, Not Prediction" })}
                                  description={t("seo.mission.description", {
                                    defaultValue: "RandoStat helps users understand lottery randomness with clear historical statistics and responsible framing.",
                                  })}
                                  path="mission"
                                  structuredData={missionStructuredData}
                                  element={<Mission />}
                                />
                              )}
                            />
                            <Route
                              path="terms-of-use"
                              element={(
                                <SEO
                                  title={t("seo.termsOfUse.title", { defaultValue: "Terms of Use | RandoStat" })}
                                  description={t("seo.termsOfUse.description", {
                                    defaultValue: "Read the terms governing use of RandoStat services and content.",
                                  })}
                                  path="terms-of-use"
                                  structuredData={siteStructuredData}
                                  element={<Terms />}
                                />
                              )}
                            />
                            <Route
                              path="privacy-policy"
                              element={(
                                <SEO
                                  title={t("seo.privacyPolicy.title", { defaultValue: "Privacy Policy | RandoStat" })}
                                  description={t("seo.privacyPolicy.description", {
                                    defaultValue: "Learn how RandoStat handles user data, preferences, and privacy protections.",
                                  })}
                                  path="privacy-policy"
                                  structuredData={siteStructuredData}
                                  element={<Privacy />}
                                />
                              )}
                            />
                            <Route
                              path="how-it-works"
                              element={(
                                <SEO
                                  title={t("seo.howItWorks.title", { defaultValue: "How RandoStat Works | Lottery Statistics and Line Tools" })}
                                  description={t("seo.howItWorks.description", {
                                    defaultValue: "Understand how RandoStat converts historical draw data into educational number signals and tools.",
                                  })}
                                  path="how-it-works"
                                  structuredData={howItWorksStructuredData}
                                  element={<HowItWorks />}
                                />
                              )}
                            />
                            <Route
                              path="login"
                              element={(
                                <SEO
                                  title={t("seo.login.title", { defaultValue: "Login | RandoStat" })}
                                  description={t("seo.login.description", {
                                    defaultValue: "Sign in to manage your RandoStat preferences and reserved lines.",
                                  })}
                                  path="login"
                                  noindex
                                  element={<Login />}
                                />
                              )}
                            />
                            <Route
                              path="pricing"
                              element={(
                                <SEO
                                  title={t("seo.pricing.title", { defaultValue: "Pricing | RandoStat" })}
                                  description={t("seo.pricing.description", {
                                    defaultValue: "Compare RandoStat access tiers for lottery history depth and advanced tools.",
                                  })}
                                  path="pricing"
                                  structuredData={pricingStructuredData}
                                  element={<PricingPage />}
                                />
                              )}
                            />
                            <Route
                              path="checkout"
                              element={(
                                <SEO
                                  title={t("seo.checkout.title", { defaultValue: "Checkout | RandoStat" })}
                                  description={t("seo.checkout.description", {
                                    defaultValue: "Checkout flow for RandoStat plans.",
                                  })}
                                  path="checkout"
                                  noindex
                                  element={<EmbeddedCheckoutPage />}
                                />
                              )}
                            />
                            <Route
                              path="checkoutReturn"
                              element={(
                                <SEO
                                  title={t("seo.checkoutReturn.title", { defaultValue: "Checkout Confirmation | RandoStat" })}
                                  description={t("seo.checkoutReturn.description", {
                                    defaultValue: "Confirm and finalize your RandoStat subscription checkout session.",
                                  })}
                                  path="checkoutReturn"
                                  noindex
                                  element={<CheckoutReturnPage />}
                                />
                              )}
                            />
                            <Route
                              path="settings"
                              element={(
                                <SEO
                                  title={t("seo.settings.title", { defaultValue: "Settings | RandoStat" })}
                                  description={t("seo.settings.description", {
                                    defaultValue: "Manage your profile, preferences, and account settings.",
                                  })}
                                  path="settings"
                                  noindex
                                  element={<SettingsPage />}
                                />
                              )}
                            />
                            <Route
                              path="stats"
                              element={(
                                <LocaleEntryRedirect
                                  fallbackLocale={resolveLocale(currentLanguage)}
                                  resolvePreferredLotteryId={resolvePreferredLotteryId}
                                />
                              )}
                            />
                            <Route path="learn/lottery-myths-odds-randomness" element={
                              <SEO
                                title={t("seo.myths.title")}
                                description={t("seo.myths.description")}
                                path={"learn/lottery-myths-odds-randomness"}
                                structuredData={mythsStructuredData}
                                element={<LearnLotteryPage />}
                              />
                            } />
                            {/* <Route
                              path="lottery-statistics"
                              element={(
                                <LegacyLotteryStatisticsRedirect
                                  fallbackLocale={resolveLocale(currentLanguage)}
                                  resolvePreferredLotteryId={resolvePreferredLotteryId}
                                />
                              )}
                            /> */}
                            <Route
                              path=":lotterySlug/number-signals"
                              element={(
                                <NumberSignalsRoute
                                  fallbackLocale={resolveLocale(currentLanguage)}
                                  resolvePreferredLotteryId={resolvePreferredLotteryId}
                                  selectedLotteryId={selectedLotteryIdState}
                                  setSelectedLotteryId={setSelectedLotteryId}
                                />
                              )}
                            />
                            <Route
                              path=":lotterySlug"
                              element={(
                                <LotteryRoute
                                  fallbackLocale={resolveLocale(currentLanguage)}
                                  resolvePreferredLotteryId={resolvePreferredLotteryId}
                                  selectedLotteryId={selectedLotteryIdState}
                                  setSelectedLotteryId={setSelectedLotteryId}
                                />
                              )}
                            />
                          </Route>
                          {/* <Route path="/" element={<Navigate replace to={`${i18n.language || 'en'}`} />} /> */}
                          <Route path="*" element={<ErrorPage />} />
                          {/* rect-snap need this to fix flaching page problem */}
                          <Route path="shell.html" element={null} />
                        </Routes>
                      </Container>
                      <Footer />
                    </BrowserRouter>
                  </ThemeContext.Provider>
                </LoginContext.Provider>
              </UserContext.Provider>
            </GameContext.Provider>
          </Stack>
        </Container>
      </PersistQueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
